import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import type { U2000AutoCompleteSearchResult } from '../u2000Core/types/U2000AutoCompleteSearchResult';
import { u2000ExtractAutoCompleteResults } from '../u2000Core/utils/u2000ExtractAutoCompleteResults';
import {
  doFindProject,
  doFindSubproject,
  doFindTasksForProject,
  luHandleApiError,
  type U3147AutoCompletCriterias,
  type U3147FindProjectItem,
  type U3147FindSubprojectItem,
  type U3147FindTasksForProjectRequest,
  type U3147TaskItem,
} from './U3147Service';
import { NS } from './U3147';

function buildFindTasksForProjectCacheKey(body: U3147FindTasksForProjectRequest): string {
  const sub = body.subprojectId == null || String(body.subprojectId).trim() === '' ? '' : String(body.subprojectId).trim();
  return `${body.projectId.trim()}::${sub}`;
}

const findTasksForProjectResolvedCache = new Map<string, U3147TaskItem[]>();
const findTasksForProjectInflight = new Map<string, Promise<U3147TaskItem[]>>();

export function useU3147FindProjectHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3147AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3147FindProjectItem>> => {
      try {
        const response = await doFindProject(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3147FindProjectItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

export interface U3147FindSubprojectHandlersParams {
  getProjectId: () => string | undefined | null;
}

export function useU3147FindSubprojectHandlers(params: U3147FindSubprojectHandlersParams) {
  const { getProjectId } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3147AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3147FindSubprojectItem>> => {
      try {
        const projectId = getProjectId();
        const criteriasWithProject =
          projectId != null && projectId.trim() !== ''
            ? [...criterias, { fieldName: 'projectId', value: projectId }]
            : criterias;
        const response = await doFindSubproject(axios, criteriasWithProject);
        return u2000ExtractAutoCompleteResults<U3147FindSubprojectItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast, getProjectId],
  );

  return { search };
}

export function useU3147FindTasksForProjectHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const fetchTasks = useCallback(
    async (body: U3147FindTasksForProjectRequest): Promise<U3147TaskItem[]> => {
      const key = buildFindTasksForProjectCacheKey(body);
      const cached = findTasksForProjectResolvedCache.get(key);
      if (cached !== undefined && cached.length > 0) return cached;

      const existing = findTasksForProjectInflight.get(key);
      if (existing) return existing;

      const promise = (async (): Promise<U3147TaskItem[]> => {
        try {
          const response = await doFindTasksForProject(axios, body);
          const raw = response.data;
          const results = Array.isArray(raw) ? raw : (raw as { results?: unknown })?.results;
          const list = Array.isArray(results) ? (results as U3147TaskItem[]) : [];
          // Ne pas mettre en cache une liste vide (souvent sous-projet manquant) — évite de bloquer un enrichissement ultérieur.
          if (list.length > 0) {
            findTasksForProjectResolvedCache.set(key, list);
          }
          return list;
        } catch (error: unknown) {
          const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
          if (errorResult.genericErrors?.length) {
            errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
          }
          return [];
        } finally {
          findTasksForProjectInflight.delete(key);
        }
      })();

      findTasksForProjectInflight.set(key, promise);
      return promise;
    },
    [axios, t, toast],
  );

  return { fetchTasks };
}
